/**
 * sessionEvents — lightweight module-level event emitter for cross-cutting
 * session lifecycle events.
 *
 * Used to decouple axios interceptors (outside React) from the AuthLayout
 * React component (which subscribes and handles navigation + banner display).
 *
 * If no subscriber is registered (e.g. during unit tests or outside the
 * authenticated layout), the interceptor falls back to window.location.assign.
 */

export type SessionEventType = 'SESSION_EXPIRED';

export interface SessionExpiredEvent {
  type: 'SESSION_EXPIRED';
  /** The path that was being accessed when the session expired */
  returnUrl?: string;
}

export type SessionEvent = SessionExpiredEvent;

type SessionEventListener = (event: SessionEvent) => void;

const listeners = new Set<SessionEventListener>();

export const sessionEvents = {
  /** Subscribe to session events. Returns an unsubscribe function. */
  on(listener: SessionEventListener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** Emit a session event to all registered listeners. */
  emit(event: SessionEvent): void {
    if (listeners.size > 0) {
      listeners.forEach((l) => l(event));
    } else {
      // Fallback: no React subscriber mounted (e.g. outside AuthLayout).
      // Redirect directly — this handles edge cases like background tabs.
      if (typeof window !== 'undefined') {
        const returnUrl = event.returnUrl ?? window.location.pathname;
        const encoded = encodeURIComponent(returnUrl);
        window.location.assign(`/login?expired=1&returnUrl=${encoded}`);
      }
    }
  },
};
